import React, { useState, useLayoutEffect, PropsWithChildren, useRef } from 'react'
import { Path, useNavigate } from 'react-router';

import {
  useAppSelector,
  useAppDispatch,
  clearPendingRedirect,
  cleanOidcUrlParams,
  devLogger
} from '../internal'


interface IProps {
  // This component does not take any props, but we define it for consistency
}

const PendingRedirectContainer: React.FC<PropsWithChildren<IProps>> = (props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const redirectDelayRef = useRef<NodeJS.Timeout | null>(null);

  const $pendingRedirectDelay: number = useAppSelector(state => state.pendingRedirect.delay);
  const $pendingRedirectFullUrl: string | null = useAppSelector(state => state.pendingRedirect.fullUrl);
  const $pendingRedirectPriority: number | null = useAppSelector(state => state.pendingRedirect.priority);
  const $pendingRedirectPathname: string = useAppSelector(state => state.pendingRedirect.pathname);
  const $pendingRedirectUseReplace: boolean | undefined = useAppSelector(state => state.pendingRedirect.useHistoryReplace);
  const $pendingRedirectQueryParams: string = useAppSelector(state => state.pendingRedirect.search);
  
  const _navigateRelative = (goTo: string, useReplace: boolean = false) => {
    if (useReplace) {
      navigate(goTo);
    } else {
      // var to = goTo;
      const toNav: Partial<Path> = { pathname: goTo };
      if ($pendingRedirectQueryParams) {
        toNav.search = $pendingRedirectQueryParams;
      } 
      navigate(toNav);
    }
  }

  const _redirectAccordingToConfig = () => {
    if ($pendingRedirectFullUrl) {
      window.location.href = $pendingRedirectFullUrl;
    } else if ($pendingRedirectPathname) {
      _navigateRelative($pendingRedirectPathname, $pendingRedirectUseReplace);
    }
    dispatch(clearPendingRedirect());
  }

  useLayoutEffect(() => {
    if ($pendingRedirectFullUrl || $pendingRedirectPathname) {
      cleanOidcUrlParams();

      devLogger(`portal redirect [${$pendingRedirectPriority ?? 'default'}]
        - from: ${window.location.hash?.slice(0,40)}
        - to: ${JSON.stringify($pendingRedirectFullUrl ?? $pendingRedirectPathname).slice(0,40)}
      `);

      if (redirectDelayRef.current) {
        clearTimeout(redirectDelayRef.current);
      }

      if (!window.location.href.includes('#/login')) {
        // as long as we're not already on the outbound login page, attempt to redirect
        // otherwise this will clear continueTo and intended post-login redirects
        if ($pendingRedirectDelay === 0 || $pendingRedirectDelay === null) {
          _redirectAccordingToConfig();
        } else {
          redirectDelayRef.current = setTimeout(_redirectAccordingToConfig, $pendingRedirectDelay);
        }
      }
    }

    // when this commponent unmounts (shouldnt really happen 
    // since it is so high up) we clear any existing redirect timeout 
    return () => {
      if (redirectDelayRef.current) {
        clearTimeout(redirectDelayRef.current);
      }
    }
  }, [
    $pendingRedirectDelay,
    $pendingRedirectFullUrl,
    $pendingRedirectPathname,
    $pendingRedirectPriority
  ])

  return null;
}

export default PendingRedirectContainer;
